'use client';
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as ProductDetail from '.';
import { ItemAddonBehaviour } from '@order-mage/integration-sdk';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslations } from 'next-intl';
import {
    ComponentProps,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useWatch } from 'react-hook-form';
import { Fragment } from 'react/jsx-runtime';
import {
    useCartStore,
    getContainedSinglePickHash,
    getHashFromItemIdMap,
    getMemberHash,
    getPriceValueTranslation,
    variantPriceDelta,
} from '@/entities/cart';
import { useStorefrontItemsIndex } from '@/entities/product/lib/useStorefrontMenu';
import { cn } from '@/shared/lib/cn';
import { EventService } from '@/shared/lib/event-service';
import { compareAssets, getMainAsset } from '@/shared/lib/getMainAsset';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Currency } from '@/shared/model/Currency';
import { LocalizedString } from '@/shared/model/Language';
import { PriceValue } from '@/shared/model/PriceValue';
import { AmountInput } from '@/shared/ui/amount-input';
import { AnimatedPrice } from '@/shared/ui/animated-price';
import { Button } from '@/shared/ui/button';
import { FieldMessage } from '@/shared/ui/field-message';
import { Form } from '@/shared/ui/form';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { Media } from '@/shared/ui/media';
import { Modal } from '@/shared/ui/modal';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { useModalContext } from '@/shared/ui/modal/modal-context';
import { ModalProps } from '@/shared/ui/modal/types';
import { ProductDetailAddonsAmountItem } from './product-detail-addons-amount-item';
import { ProductDetailAddonsRadioItemRow } from './product-detail-addons-radio-item-row';
import { ProductDetailTags } from './product-detail-tags';
import './product-detail-v2.scss';

import { MAX_ADDON_NESTING_DEPTH } from '@/api/menu-list/max-addon-nesting-depth';
import {
    getMemberVariantChoices,
    getSectionSelectedTotal,
    isMemberOpeningMore,
    memberAmount,
    nestedAddonSource,
    type StepSection,
    type StepSelections,
} from './addon-step.utils';
import {
    ProductDetailStepsGate,
    ProductDetailStepsProgress,
    ProductDetailStepsSummary,
    useProductDetailSteps,
} from './product-detail-steps';
import { ProductDetailContext, ProductDetailProvider } from './provider';
import {
    listVisibleAddonSections,
    useVisibleAddonSections,
    type VisibleAddonSection,
} from './use-visible-addon-sections';

const packagePriceOf = (
    prices: {
        packagePrices: Array<{ priceValue: { amount: string; currency: string } }>;
    } | null,
): PriceValue | undefined => {
    const list = prices?.packagePrices ?? [];
    const amount = list.reduce((sum, pkg) => sum + Number(pkg.priceValue.amount), 0);
    const currency = list[0]?.priceValue.currency;
    if (!(amount > 0) || !currency) return undefined;
    return { amount: String(amount), currency };
};

type Props = ModalProps & {
    hash: string;
    id?: string;
    initialQuantity?: number;
    initialVariant?: string;
    initialAddons?: Record<string, string | Record<string, number>>;
    initialMemberVariants?: Record<string, string>;
    initialNote?: string;
    layout?: string;
    menuUsesSteps?: boolean;
    initialStep?: number;
    children?: ReactNode;
};

const ProductDetailModalImage = ({
    imageProps,
}: {
    imageProps?: Omit<ComponentProps<typeof Media>, 'data'>;
}) => {
    const getLocalizedValue = useGetLocalizedValue();
    const { data, currentItem } = useContext(ProductDetailContext);
    const itemName = getLocalizedValue(data?.name as any);
    const imageAssets = [...(currentItem?.assets ?? [])].sort(compareAssets);
    const isScrollable = imageAssets.length > 1;
    const [emblaRef, emblaApi] = useEmblaCarousel({
        active: isScrollable,
        loop: false,
        align: 'start',
    });
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const handleSlideButtonClick = useCallback(
        (index: number) => {
            if (!emblaApi) return;
            emblaApi.scrollTo(index);
        },
        [emblaApi],
    );
    const onInit = useCallback((api: EmblaCarouselType) => {
        setScrollSnaps(api.scrollSnapList());
    }, []);
    const onSelect = useCallback((api: EmblaCarouselType) => {
        setSelectedIndex(api.selectedScrollSnap());
    }, []);

    useEffect(() => {
        if (!emblaApi) return;
        const frame = requestAnimationFrame(() => {
            onInit(emblaApi);
            onSelect(emblaApi);
        });

        emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);

        return () => {
            cancelAnimationFrame(frame);
            emblaApi.off('reInit', onInit).off('reInit', onSelect).off('select', onSelect);
        };
    }, [emblaApi, onInit, onSelect]);

    if (!imageAssets.length) {
        return null;
    }

    if (!isScrollable) {
        return (
            <div className="product-detail-v2__image-wrapper">
                <Media
                    data={{ url: imageAssets[0].fullUrl ?? '', alt: itemName || '' }}
                    width={368}
                    height={368}
                    data-testid="product-detail-image"
                    data-value={imageAssets[0].id}
                    {...imageProps}
                    className={cn('product-detail-v2__image', imageProps?.className)}
                />
                <ProductDetailTags variant="inImage" />
            </div>
        );
    }

    return (
        <div className="product-detail-v2__image-carousel" data-testid="product-detail-image-carousel">
            <div
                ref={emblaRef}
                className="product-detail-v2__image-viewport"
                data-testid="product-detail-image-viewport"
            >
                <div className="product-detail-v2__image-container">
                    {imageAssets.map((image, index) => (
                        <div
                            key={image.id}
                            className="product-detail-v2__image-slide"
                            data-testid="product-detail-image-slide"
                            data-slide={index + 1}
                        >
                            <Media
                                data={{ url: image.fullUrl ?? '', alt: itemName || '' }}
                                width={368}
                                height={368}
                                data-testid="product-detail-image"
                                data-value={image.fullUrl}
                                {...imageProps}
                                className={cn('product-detail-v2__image', imageProps?.className)}
                            />
                        </div>
                    ))}
                </div>
                <div
                    className="product-detail-v2__image-controls"
                    data-testid="product-detail-image-controls"
                >
                    <div className="product-detail-v2__image-dots" data-testid="product-detail-image-dots">
                        {scrollSnaps.map((_, index) => (
                            <button
                                key={index}
                                type="button"
                                className={cn(
                                    'product-detail-v2__image-dot',
                                    index === selectedIndex && 'product-detail-v2__image-dot--active',
                                )}
                                onClick={() => handleSlideButtonClick(index)}
                                aria-label={`Go to slide ${index + 1}`}
                                data-testid="product-detail-image-dot"
                                data-slide={index + 1}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ProductDetailModalTitle = () => {
    return <ProductDetail.Info.Name size="2xl" as={Fragment} />;
};

const ProductDetailModalDescription = () => {
    return <ProductDetail.Info.Description as={Modal.Description} />;
};

const ProductDetailModalAddons = () => {
    const t = useTranslations();
    const getLocalizedValue = useGetLocalizedValue();
    const { currentItem, formMethods } = useContext(ProductDetailContext);
    const getItemPrices = useCartStore((s) => s.getItemPrices);
    const itemsMap = useStorefrontItemsIndex();
    const idToHash = useMemo(() => getHashFromItemIdMap(itemsMap), [itemsMap]);

    const visibleSections = useVisibleAddonSections(currentItem);
    const formValues = useWatch({ control: formMethods.control });
    const motionReady = useRef(false);
    const [closedSections, setClosedSections] = useState<Record<string, boolean>>({});
    const setSectionClosed = useCallback((id: string, next: boolean) => {
        setClosedSections((prev) => (prev[id] === next ? prev : { ...prev, [id]: next }));
    }, []);
    useEffect(() => {
        motionReady.current = true;
    }, []);

    const sourceBody = (body: NonNullable<(typeof itemsMap)[string]>['body'], chosenId?: string) =>
        nestedAddonSource(body, body.variants ?? [], chosenId, (itemId) => {
            const variantHash = idToHash.get(itemId);
            return variantHash ? itemsMap[variantHash]?.body : undefined;
        });

    const memberOpensMore = (hash: string) => {
        const body = itemsMap[hash]?.body;
        if (!body) return false;
        const source = sourceBody(body, formValues.memberVariants?.[body.id]);
        return isMemberOpeningMore(
            getMemberVariantChoices(body.variants ?? []).length,
            listVisibleAddonSections(source, itemsMap, getLocalizedValue, getItemPrices).length,
        );
    };

    // Render SINGLE_PICK addon members (radio group)
    const renderSinglePickMembers = (
        addonDef: VisibleAddonSection['addonDef'],
        members: VisibleAddonSection['members'],
        depth: number,
    ) => {
        const minAmount = addonDef.minAmount ?? 0;
        const currentAddonValue = formValues.addons?.[addonDef.id || ''] as string | undefined;
        const defaultAddonValue = getContainedSinglePickHash(members, idToHash);

        return (
            <Form.RadioGroup
                name={`addons.${addonDef.id}`}
                className="product-detail-v2__radio-group"
                allowDeselect={minAmount === 0}
            >
                {[...members]
                    .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
                    .map((member) => {
                        const hash = getMemberHash(member, idToHash);
                        const item = hash ? itemsMap[hash] : undefined;
                        const mainAsset = getMainAsset(item?.body.assets ?? [])?.fullUrl;
                        // The contained member is part of the item, so it is free.
                        const isContained = hash !== undefined && hash === defaultAddonValue;
                        const priceRaw = hash && item ? getItemPrices(hash) : null;
                        const price =
                            isContained && priceRaw
                                ? {
                                      ...priceRaw,
                                      itemPrice: {
                                          ...priceRaw.itemPrice,
                                          amount: '0',
                                      },
                                  }
                                : priceRaw;

                        const isEdited =
                            currentAddonValue !== defaultAddonValue && hash === currentAddonValue;

                        const variantRows = (item?.body.variants ?? []) as Array<{
                            id: string;
                            itemId: string;
                            isDefault?: boolean;
                        }>;
                        const chosenVariantId = item ? formValues.memberVariants?.[item.body.id] : undefined;
                        const activeVariant = variantRows.find((row) => row.id === chosenVariantId);
                        const variantHash =
                            activeVariant && !activeVariant.isDefault
                                ? idToHash.get(activeVariant.itemId)
                                : undefined;
                        const packageSource = variantHash ? getItemPrices(variantHash) : priceRaw;

                        return (
                            <div
                                key={hash}
                                className="product-detail__member-block"
                                data-member-block
                                data-opens-more={hash && memberOpensMore(hash) ? 'true' : 'false'}
                            >
                                <ProductDetailAddonsRadioItemRow
                                    value={hash ?? ''}
                                    imageUrl={mainAsset ?? undefined}
                                    name={getLocalizedValue(item?.body.name as LocalizedString) ?? ''}
                                    description={getLocalizedValue(item?.body.description as LocalizedString)}
                                    priceValue={price?.itemPrice as PriceValue}
                                    packagePrice={packagePriceOf(packageSource)}
                                    isEdited={isEdited}
                                    contained={isContained ? 1 : 0}
                                    amount={hash === currentAddonValue ? 1 : 0}
                                />
                                {renderMemberNest(hash, hash === currentAddonValue, depth)}
                            </div>
                        );
                    })}
            </Form.RadioGroup>
        );
    };

    // Render MULTI_PICK addon members (amount inputs)
    const renderMultiPickMembers = (
        addonDef: VisibleAddonSection['addonDef'],
        members: VisibleAddonSection['members'],
        depth: number,
    ) => {
        const sectionHasImage = members.some((member) => {
            const hash = getMemberHash(member, idToHash);
            const item = hash ? itemsMap[hash] : undefined;
            return Boolean(getMainAsset(item?.body.assets ?? [])?.fullUrl);
        });

        return [...members]
            .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
            .map((member) => {
                const hash = getMemberHash(member, idToHash);
                const item = hash ? itemsMap[hash] : undefined;
                const mainAsset = getMainAsset(item?.body.assets ?? [])?.fullUrl;
                const priceRaw = hash && item ? getItemPrices(hash) : null;
                const containedAmount = Number(member.amount?.value ?? 0);

                const currentMemberValue =
                    (formValues.addons?.[addonDef.id || ''] as Record<string, number | boolean>)?.[
                        hash || ''
                    ] ?? 0;
                const normalizedCurrentValue =
                    typeof currentMemberValue === 'boolean'
                        ? currentMemberValue
                            ? 1
                            : 0
                        : Number(currentMemberValue ?? 0);

                const isEdited = normalizedCurrentValue !== containedAmount;

                const price = priceRaw;

                // The section's min and max never lock a control: the customer can always change a
                // pick, and an unmet bound shows as an issue under the section and blocks the cart.
                // Only the catalog's floor below the contained amount is a hard limit.
                const restrictedFloor = Number(member.amount?.value ?? 0);
                const isRestrictedBelowDefined =
                    member.restrictedAmountBelowDefined === true && normalizedCurrentValue <= restrictedFloor;
                const disableMinus = normalizedCurrentValue <= 0 || isRestrictedBelowDefined;
                const disableUncheck = isRestrictedBelowDefined;
                const variantRows = (item?.body.variants ?? []) as Array<{
                    id: string;
                    itemId: string;
                    isDefault?: boolean;
                }>;
                const chosenVariantId = item ? formValues.memberVariants?.[item.body.id] : undefined;
                const activeVariant = variantRows.find((row) => row.id === chosenVariantId);
                const variantHash =
                    activeVariant && !activeVariant.isDefault
                        ? idToHash.get(activeVariant.itemId)
                        : undefined;
                const packageSource = variantHash ? getItemPrices(variantHash) : priceRaw;

                return (
                    <div
                        key={hash || member.itemId}
                        className="product-detail__member-block"
                        data-member-block
                        data-opens-more={hash && memberOpensMore(hash) ? 'true' : 'false'}
                    >
                        <ProductDetailAddonsAmountItem
                            imageUrl={mainAsset ?? undefined}
                            showImage={sectionHasImage}
                            name={`addons.${addonDef.id}.${hash}`}
                            title={getLocalizedValue(item?.body.name as LocalizedString)}
                            description={getLocalizedValue(item?.body.description as LocalizedString)}
                            priceValue={{
                                amount: price?.itemPrice.amount as string,
                                currency: price?.itemPrice.currency as string,
                            }}
                            packagePrice={packagePriceOf(packageSource)}
                            maxSelection={
                                addonDef.behaviour === ItemAddonBehaviour.MULTI_PICK
                                    ? 1
                                    : (addonDef.maxAmount ?? undefined)
                            }
                            isEdited={isEdited}
                            contained={containedAmount}
                            amount={normalizedCurrentValue}
                            disableMinus={disableMinus}
                            disableUncheck={disableUncheck}
                        />
                        {renderMemberNest(hash, normalizedCurrentValue > 0, depth)}
                    </div>
                );
            });
    };

    // Render a single addon section (used for both SINGLE type and nested GROUP members)
    const renderMemberNest = (memberHash: string | undefined, selected: boolean, depth: number) => {
        if (!selected || !memberHash || depth + 1 >= MAX_ADDON_NESTING_DEPTH) return null;
        const body = itemsMap[memberHash]?.body;
        if (!body) return null;
        const rows = body.variants ?? [];
        const choices = getMemberVariantChoices(rows);
        const chosen = formValues.memberVariants?.[body.id];
        const source = sourceBody(body, chosen);
        const sections = listVisibleAddonSections(source, itemsMap, getLocalizedValue, getItemPrices);
        if (choices.length === 0 && sections.length === 0) return null;
        const chosenChoice = choices.find((row) => row.id === chosen);
        const defaultRow = rows.find((row) => row.isDefault);
        const defaultHash = defaultRow ? idToHash.get(defaultRow.itemId) : undefined;
        // Without a default row in the snapshot the default is the member itself.
        const defaultName =
            (defaultHash ? getLocalizedValue(itemsMap[defaultHash]?.body.name as LocalizedString) : '') ||
            getLocalizedValue(body.name as LocalizedString);
        const selectVariant = (rowId: string | undefined) => {
            const next = { ...(formValues.memberVariants ?? {}) };
            // No entry is the default, the same as a member nobody has touched.
            if (rowId) next[body.id] = rowId;
            else delete next[body.id];
            formMethods.setValue('memberVariants', next as Record<string, string>);
        };

        const markMotion = (motionClass: string) => (node: HTMLDivElement | null) => {
            if (node && motionReady.current) node.classList.add(motionClass);
        };

        return (
            <>
                {choices.length > 0 && (
                    <div
                        className="product-detail__member-variants"
                        data-testid="product-detail-member-variants"
                        ref={markMotion('product-detail__member-variants--motion')}
                    >
                        <span className="product-detail__member-variants-label">
                            {t('PRODUCT_DETAIL.VARIANT')}
                        </span>
                        <div className="product-detail__member-variant-pills">
                            <button
                                type="button"
                                data-testid="product-detail-member-variant"
                                data-default="true"
                                className={cn(
                                    'product-detail__member-variant-pill',
                                    !chosenChoice && 'product-detail__member-variant-pill--selected',
                                )}
                                onClick={() => selectVariant(undefined)}
                            >
                                {defaultName}
                            </button>
                            {choices.map((row) => {
                                const hash = idToHash.get(row.itemId);
                                const variantItem = hash ? itemsMap[hash] : undefined;
                                const delta = variantPriceDelta(getItemPrices, hash, memberHash);
                                const currency = hash ? getItemPrices(hash)?.itemPrice.currency : undefined;
                                return (
                                    <button
                                        key={row.id}
                                        type="button"
                                        data-testid="product-detail-member-variant"
                                        className={cn(
                                            'product-detail__member-variant-pill',
                                            chosenChoice?.id === row.id &&
                                                'product-detail__member-variant-pill--selected',
                                        )}
                                        onClick={() => selectVariant(row.id)}
                                    >
                                        {getLocalizedValue(variantItem?.body.name as LocalizedString)}
                                        {delta !== 0 && currency && (
                                            <span
                                                className="product-detail__member-variant-pill-price"
                                                data-testid="product-detail-member-variant-price"
                                            >
                                                {`${delta > 0 ? '+ ' : ''}${getPriceValueTranslation({
                                                    amount: delta,
                                                    currency: currency as Currency,
                                                    t,
                                                })}`}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
                {sections.length > 0 && (
                    <div
                        className="product-detail__member-nest"
                        ref={markMotion('product-detail__member-nest--motion')}
                    >
                        {sections.map((section) =>
                            renderAddonSection(
                                section.addonDef,
                                section.members,
                                section.key,
                                depth + 1,
                                true,
                            ),
                        )}
                    </div>
                )}
            </>
        );
    };

    const renderAddonSection = (
        addonDef: VisibleAddonSection['addonDef'],
        members: VisibleAddonSection['members'],
        key: string,
        depth = 0,
        nested = false,
    ) => {
        if (!addonDef || !members || members.length === 0) return null;
        // The badge reads the same count as the section's bounds in the steps guide.
        const stepSection: StepSection = {
            id: addonDef.id,
            behaviour: addonDef.behaviour,
            members: members.map((member) => ({
                hash: getMemberHash(member, idToHash) ?? '',
                hasNested: false,
            })),
        };
        const selections = (formValues.addons ?? {}) as StepSelections;
        const selectedCount = getSectionSelectedTotal(stepSection, selections);
        const minAmount = addonDef.minAmount ?? 0;
        const maxAmount = addonDef.maxAmount as number | null | undefined;
        // An empty optional section stays open. A real pick finishes it the same way as a required one.
        const done =
            selectedCount > 0 &&
            selectedCount >= minAmount &&
            (maxAmount == null || selectedCount <= maxAmount);

        const memberQuantity = (member: { hash?: string; itemId?: string }) => {
            const hash = getMemberHash(member, idToHash);
            return hash ? memberAmount(stepSection, selections, hash) : 0;
        };
        const pickedNames = members.flatMap((member: { hash?: string; itemId?: string }) => {
            const hash = getMemberHash(member, idToHash);
            const qty = memberQuantity(member);
            if (!hash || qty <= 0) return [];
            const item = itemsMap[hash];
            if (!item) return [];
            let label = getLocalizedValue(item.body.name as LocalizedString);
            const chosen = formValues.memberVariants?.[item.body.id];
            const active = (item.body.variants ?? []).find((row) => row.id === chosen);
            if (active && !active.isDefault) {
                const variantHash = idToHash.get(active.itemId);
                const variantName = variantHash
                    ? getLocalizedValue(itemsMap[variantHash]?.body.name as LocalizedString)
                    : '';
                if (variantName) label = `${label} - ${variantName}`;
            }
            return [qty > 1 ? `${qty}x ${label}` : label];
        });
        const collapsible =
            !nested &&
            addonDef.behaviour === ItemAddonBehaviour.SINGLE_PICK &&
            done &&
            pickedNames.length > 0 &&
            members.every((member: { hash?: string; itemId?: string }) => {
                const hash = getMemberHash(member, idToHash);
                return !hash || memberQuantity(member) <= 0 || !memberOpensMore(hash);
            });

        return (
            <ProductDetail.Addons.AddonSection
                key={key}
                id={nested ? undefined : `product-detail-addon-section-${addonDef.id}`}
                data-addon-id={addonDef.id}
                className="product-detail-v2__addon-section"
                version="box"
                nested={nested}
                done={done}
                collapsible={collapsible}
                closed={closedSections[addonDef.id] === true}
                onClosedChange={(next) => setSectionClosed(addonDef.id, next)}
                picked={pickedNames.join(' · ')}
                selectedCount={selectedCount}
                count={members.length}
                name={getLocalizedValue((addonDef.name ?? {}) as LocalizedString)}
                isRequired={minAmount > 0}
                minSelection={
                    addonDef.behaviour === ItemAddonBehaviour.SINGLE_PICK
                        ? undefined
                        : (addonDef.minAmount ?? undefined)
                }
                maxSelection={
                    addonDef.behaviour === ItemAddonBehaviour.SINGLE_PICK
                        ? undefined
                        : (addonDef.maxAmount ?? undefined)
                }
            >
                {addonDef.behaviour === ItemAddonBehaviour.SINGLE_PICK
                    ? renderSinglePickMembers(addonDef, members, depth)
                    : renderMultiPickMembers(addonDef, members, depth)}
            </ProductDetail.Addons.AddonSection>
        );
    };

    const steps = useProductDetailSteps();
    const stepping = steps?.mode === 'steps';
    if (!stepping && visibleSections.length === 0) return null;

    let shown = visibleSections;
    if (stepping && steps.isReview) shown = [];
    else if (stepping) shown = visibleSections.filter((_, index) => index === steps.step);

    return (
        <>
            <ProductDetailStepsProgress />
            {stepping && steps.isReview ? (
                <ProductDetailStepsSummary />
            ) : (
                <div
                    key={stepping ? steps.step : 'column'}
                    className={stepping ? 'product-detail-steps__body' : undefined}
                    data-testid={stepping ? 'product-detail-steps-body' : undefined}
                >
                    <ProductDetail.Addons>
                        {shown.map(({ key, addonDef, members }) =>
                            renderAddonSection(addonDef, members, key),
                        )}
                    </ProductDetail.Addons>
                </div>
            )}
        </>
    );
};

const ProductDetailModalQuantityInput = () => {
    const { formMethods } = useContext(ProductDetailContext);
    const quantity = useWatch({ control: formMethods.control, name: 'quantity' });

    return (
        <AmountInput
            value={quantity}
            min={1}
            data-testid="product-detail-quantity-input"
            wrapperProps={{
                'data-testid': 'product-detail-quantity-input-wrapper',
            }}
            plusButtonProps={{
                'data-testid': 'product-detail-quantity-increase',
                onClick: () => {
                    formMethods.setValue('quantity', quantity + 1);
                },
            }}
            minusButtonProps={{
                'data-testid': 'product-detail-quantity-decrease',
                onClick: () => {
                    const finalQuantity = quantity - 1;
                    if (finalQuantity < 1) return;
                    formMethods.setValue('quantity', finalQuantity);
                },
            }}
        />
    );
};

type AddToCartButtonProps = ComponentProps<typeof Layout>;
const ProductDetailModalAddToCartButton = ({
    media,
    className,
    slot,
    ...props
}: AddToCartButtonProps & { slot?: 'steps' }) => {
    const t = useTranslations();
    const steps = useProductDetailSteps();
    const { hash, currentItem, formMethods, id } = useContext(ProductDetailContext);
    const { modalId } = useModalContext();
    const getItemFinalPrice = useCartStore((s) => s.getItemFinalPrice);
    const formValues = useWatch({ control: formMethods.control });
    const addons = useWatch({ control: formMethods.control, name: 'addons' });
    const variant = useWatch({ control: formMethods.control, name: 'variant' });
    const quantity = useWatch({ control: formMethods.control, name: 'quantity' });
    const note = useWatch({ control: formMethods.control, name: 'note' });
    const totalPrice = getItemFinalPrice(
        { variant, addons, memberVariants: formValues.memberVariants },
        hash,
        currentItem?.addons,
    );
    const amount = parseFloat(String(totalPrice?.amount || '0')) * (formValues.quantity ?? 1);
    const inSteps = steps?.mode === 'steps';
    if ((inSteps && slot !== 'steps') || (!inSteps && slot === 'steps') || (inSteps && !steps.isReview)) {
        return null;
    }

    return (
        <Layout
            element={Button}
            media={media}
            className={
                className ? `product-detail-v2__add-button ${className}` : 'product-detail-v2__add-button'
            }
            {...props}
            disabled={(steps?.blockers.length ?? 0) > 0}
            onClick={() => {
                EventService.trigger('cart:product:add-to-cart', {
                    id,
                    productHash: hash,
                    quantity: quantity ?? 1,
                    variant: formValues.variant as string,
                    addons: formValues.addons as Record<string, string | Record<string, number>>,
                    memberVariants: formValues.memberVariants as Record<string, string> | undefined,
                    note,
                    suppressModal: !!id,
                });
                ModalService.hide(modalId);
            }}
            data-testid="product-detail-add-to-cart-button"
        >
            <span
                className="product-detail-v2__add-button-text-desktop"
                data-testid="product-detail-add-to-cart-text"
            >
                {id ? t('PRODUCT_DETAIL.SAVE_EDITS') : t('WORDS.ADD_TO_CART')}
            </span>
            <span data-testid="product-detail-total-price" data-value={amount}>
                {totalPrice?.currency ? (
                    <AnimatedPrice amount={amount} currency={totalPrice.currency as Currency} />
                ) : null}
            </span>
            <Icon name="plus" width={24} height={24} className="product-detail-v2__add-button-icon-mobile" />
        </Layout>
    );
};

export const ProductDetailWrapper = ({
    hash,
    id,
    initialQuantity,
    initialVariant,
    initialAddons,
    initialMemberVariants,
    initialNote,
    children,
    ...modalProps
}: Props) => {
    return (
        <Modal
            {...modalProps}
            wrapperProps={{
                className: 'product-detail-v2__modal-wrapper',
                'data-testid': 'product-detail-modal',
            }}
        >
            <ProductDetailProvider
                hash={hash}
                id={id}
                initialQuantity={initialQuantity}
                initialVariant={initialVariant}
                initialAddons={initialAddons}
                initialMemberVariants={initialMemberVariants}
                initialNote={initialNote}
            >
                {children}
            </ProductDetailProvider>
        </Modal>
    );
};

const ATTENTION_CLASS = 'product-detail__addons-addon-section--attention';

/** Scrolls the modal to the first section box of `addonId` on screen and flashes it. */
const revealAddonSection = (from: HTMLElement | null, addonId: string) => {
    const root = from?.closest('[data-testid="product-detail-modal"]') ?? document;
    const target = root.querySelector<HTMLElement>(
        `[data-testid="product-detail-addon-section"][data-addon-id="${CSS.escape(addonId)}"]`,
    );
    if (!target) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    // Restart the flash when the same issue is clicked again before it has finished.
    target.classList.remove(ATTENTION_CLASS);
    void target.offsetWidth;
    target.classList.add(ATTENTION_CLASS);
    // The stylesheet owns the flash duration; the class comes off once it has played.
    const durationMs = parseFloat(getComputedStyle(target).animationDuration) * 1000 || 0;
    window.setTimeout(() => target.classList.remove(ATTENTION_CLASS), durationMs);
};

const ProductDetailAddonIssues = () => {
    const t = useTranslations();
    const steps = useProductDetailSteps();
    const listRef = useRef<HTMLUListElement>(null);
    const pending = useRef<string | null>(null);
    // Blockers come in screen order, so the first one is the topmost unfinished box. The next one
    // shows once this one is solved.
    const issue = steps?.blockers[0];
    const onSummary = steps?.mode === 'steps' && steps.isReview;

    useEffect(() => {
        if (!pending.current || onSummary) return;
        const addonId = pending.current;
        pending.current = null;
        // The step's sections mount in this commit; reveal once they have been laid out.
        const frame = requestAnimationFrame(() => revealAddonSection(listRef.current, addonId));
        return () => cancelAnimationFrame(frame);
    }, [steps?.step, onSummary]);

    if (!issue) return null;

    const reveal = () => {
        if (steps && steps.mode === 'steps' && (onSummary || issue.step !== steps.step)) {
            pending.current = issue.id;
            steps.goTo(issue.step);
            return;
        }
        revealAddonSection(listRef.current, issue.id);
    };

    return (
        <ul
            ref={listRef}
            className="product-detail-addon-issues"
            data-testid="product-detail-addon-issues"
            role="alert"
        >
            <li>
                <FieldMessage
                    state="error"
                    role="button"
                    tabIndex={0}
                    className="product-detail-addon-issues__button"
                    data-testid="product-detail-addon-issue"
                    data-addon-id={issue.id}
                    onClick={reveal}
                    onKeyDown={(event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        reveal();
                    }}
                >
                    {t(
                        issue.kind === 'min'
                            ? 'PRODUCT_DETAIL.SELECT_AT_LEAST'
                            : 'PRODUCT_DETAIL.MAX_REACHED',
                        { name: issue.label, count: issue.bound },
                    )}
                </FieldMessage>
            </li>
        </ul>
    );
};

const ProductDetailModalFooter = () => {
    const t = useTranslations();
    const steps = useProductDetailSteps();
    if (steps?.mode === 'steps') {
        const toSummary = !steps.isReview && steps.step === steps.reviewIndex - 1;
        return (
            <div className="product-detail-steps__footer">
                <ProductDetailAddonIssues />
                <div className="product-detail-steps__nav" data-testid="product-detail-steps-footer">
                    <Layout
                        element={Button}
                        type="button"
                        variant="secondary"
                        className="product-detail-steps__back"
                        data-testid="product-detail-step-back"
                        disabled={steps.step === 0}
                        onClick={steps.goBack}
                        style={{ height: '44px', boxSizing: 'border-box', flexShrink: 0 }}
                        media={{ initial: { height: '2.75rem', boxSizing: 'border-box', flexShrink: 0 } }}
                    >
                        {t('WORDS.BACK')}
                    </Layout>
                    {steps.isReview ? (
                        <ProductDetailModalAddToCartButton
                            slot="steps"
                            style={{ height: '44px', boxSizing: 'border-box', flexGrow: 1 }}
                            media={{ initial: { height: '2.75rem', boxSizing: 'border-box', flexGrow: 1 } }}
                        />
                    ) : (
                        <Layout
                            element={Button}
                            type="button"
                            className="product-detail-steps__next"
                            data-testid="product-detail-step-next"
                            disabled={!steps.canContinue}
                            onClick={steps.goNext}
                            style={{ height: '44px', boxSizing: 'border-box', flexGrow: 1 }}
                            media={{ initial: { height: '2.75rem', boxSizing: 'border-box', flexGrow: 1 } }}
                        >
                            {toSummary ? t('PRODUCT_DETAIL.TO_SUMMARY') : t('WORDS.CONTINUE')}
                        </Layout>
                    )}
                </div>
            </div>
        );
    }
    return (
        <div className="product-detail-v2__footer">
            <ProductDetailAddonIssues />
            <div className="product-detail-v2__footer-actions">
                <ProductDetailModalQuantityInput />
                <ProductDetailModalAddToCartButton
                    media={{ initial: { height: '2.75rem', boxSizing: 'border-box', flexGrow: 1 } }}
                />
            </div>
        </div>
    );
};

/** Provider that only provides context and renders children (Modal prefab). Use with prefab: "Modal" as child. */
export const ProductDetailModalProvider = ({
    hash,
    id,
    initialQuantity,
    initialVariant,
    initialAddons,
    initialMemberVariants,
    initialNote,
    layout,
    menuUsesSteps,
    initialStep,
    children,
}: Props) => {
    return (
        <ProductDetailProvider
            hash={hash}
            id={id}
            initialQuantity={initialQuantity}
            initialVariant={initialVariant}
            initialAddons={initialAddons}
            initialMemberVariants={initialMemberVariants}
            initialNote={initialNote}
        >
            <ProductDetailStepsGate layout={layout} menuUsesSteps={menuUsesSteps} initialStep={initialStep}>
                {children}
            </ProductDetailStepsGate>
        </ProductDetailProvider>
    );
};

export {
    ProductDetailWrapper as Wrapper,
    ProductDetailModalProvider as Provider,
    ProductDetailModalImage as Image,
    ProductDetailModalTitle as Title,
    ProductDetailModalDescription as Description,
    ProductDetailModalAddons as AddonsList,
    ProductDetailModalQuantityInput as QuantityInput,
    ProductDetailModalAddToCartButton as AddToCartButton,
    ProductDetailModalFooter as Footer,
};

export { ProductDetailWrapper as ProductDetailModal };
